import { useState, useEffect, useCallback, useRef, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { FiLoader, FiArrowLeft, FiCheck } from "react-icons/fi";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { SEO } from "@/components/seo";
import { useAuth, useUser } from "./_hooks/useAuth";
import { OTPInput } from "./_components/OTPInput";

export function VerifyPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get("redirect") || "/portal";

  const { verifyCode, requestCode, isLoading, isAuthenticated } = useAuth();
  const user = useUser();

  const [code, setCode] = useState("");
  const [email, setEmail] = useState("");
  const [resendCountdown, setResendCountdown] = useState(0);
  const [devCode, setDevCode] = useState<string | null>(null);

  // Track if we've already submitted this code to prevent double-submission
  const submittedCodeRef = useRef<string | null>(null);

  // Get email and dev code from session storage
  useEffect(() => {
    const storedEmail = sessionStorage.getItem("auth_email");
    const storedDevCode = sessionStorage.getItem("auth_dev_code");

    if (storedEmail) {
      setEmail(storedEmail);
    } else {
      // No email stored, redirect back to login
      navigate("/login", { replace: true });
    }

    if (storedDevCode) {
      setDevCode(storedDevCode);
      // Clear it after reading
      sessionStorage.removeItem("auth_dev_code");
    }
  }, [navigate]);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      // Clear stored email
      sessionStorage.removeItem("auth_email");
      // Redirect admins to /admin, customers to /portal (or custom redirect)
      const targetPath = user.type === "admin" ? "/admin" : redirect;
      navigate(targetPath, { replace: true });
    }
  }, [isAuthenticated, user, navigate, redirect]);

  // Countdown timer for resend
  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => setResendCountdown(resendCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCountdown]);

  const handleSubmit = useCallback(async (e?: FormEvent) => {
    e?.preventDefault();

    if (code.length !== 6) {
      toast.error("Please enter the 6-digit code");
      return;
    }

    // Prevent double-submission of the same code
    if (submittedCodeRef.current === code) {
      return;
    }
    submittedCodeRef.current = code;

    const result = await verifyCode(email, code);

    if (result.success) {
      toast.success("Welcome back!");
      // Navigation handled by useEffect
    } else {
      toast.error(result.error || "Invalid code");
      setCode(""); // Clear code on error
      submittedCodeRef.current = null; // Allow retry with new code
    }
  }, [code, email, verifyCode]);

  // Auto-submit when 6 digits entered
  useEffect(() => {
    if (code.length === 6 && !isLoading && submittedCodeRef.current !== code) {
      handleSubmit();
    }
  }, [code, isLoading, handleSubmit]);

  const handleResend = async () => {
    if (resendCountdown > 0) return;

    const result = await requestCode(email);

    if (result.success) {
      toast.success("New code sent to your email");
      setResendCountdown(60); // 60 second cooldown
      setCode(""); // Clear current code
    } else {
      toast.error(result.error || "Failed to resend code");
    }
  };

  // Mask email for display
  const maskedEmail = email
    ? email.replace(/(.{2})(.*)(@.*)/, (_, start, middle, end) => {
        return start + "*".repeat(Math.min(middle.length, 5)) + end;
      })
    : "";

  return (
    <>
      <SEO
        title="Verify Code"
        description="Enter your verification code to sign in to your Path Financial Solutions account."
        url="/login/verify"
        noIndex
      />

      <div className="min-h-[calc(100vh-200px)] flex items-center justify-center px-4 py-16">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 sm:p-10">
            {/* Logo */}
            <div className="text-center mb-8">
              <Link to="/" className="inline-block">
                <span className="text-xl font-bold tracking-tight text-primary">
                  PATH FINANCIAL
                </span>
              </Link>
            </div>

            {/* Header */}
            <div className="text-center mb-8">
              <h1 className="font-serif text-3xl font-medium text-primary mb-2">
                Check your email
              </h1>
              <p className="text-muted-foreground">
                We sent a code to{" "}
                <span className="font-medium text-foreground">{maskedEmail}</span>
              </p>
            </div>

            {/* Dev code display */}
            {devCode && (
              <div className="mb-6 p-4 bg-yellow-100 border border-yellow-300 rounded-lg text-center">
                <p className="text-sm text-yellow-800 font-medium">DEV MODE</p>
                <p className="text-2xl font-mono font-bold text-yellow-900">{devCode}</p>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-4">
                <OTPInput
                  value={code}
                  onChange={setCode}
                  disabled={isLoading}
                />
              </div>

              <Button
                type="submit"
                className="w-full btn-pill"
                size="lg"
                disabled={isLoading || code.length !== 6}
              >
                {isLoading ? (
                  <>
                    <FiLoader className="animate-spin mr-2" />
                    Verifying...
                  </>
                ) : (
                  <>
                    <FiCheck className="mr-2" />
                    Verify code
                  </>
                )}
              </Button>
            </form>

            {/* Resend code */}
            <div className="text-center mt-6">
              {resendCountdown > 0 ? (
                <p className="text-sm text-muted-foreground">
                  Resend code in {resendCountdown}s
                </p>
              ) : (
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={isLoading}
                  className="text-sm text-primary font-medium hover:underline disabled:opacity-50"
                >
                  Resend code
                </button>
              )}
            </div>

            {/* Back to login */}
            <div className="text-center mt-8 pt-6 border-t border-border">
              <Link
                to="/login"
                className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
              >
                <FiArrowLeft className="mr-2" />
                Back to login
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
